import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { BehaviorSubject } from 'rxjs';
import { BaseNumericPropertyViewModel } from '../../../../../src/lib/view-models/base-type/base-numeric-property-view-model';
import { BaseBoolPropertyViewModel } from '../../../../../src/lib/view-models/base-type/bool-property-view-model';
import { BaseDateTimeOffsetPropertyViewModel } from '../../../../../src/lib/view-models/base-type/date-time-offset-property-view-model';
import { BaseDateTimePropertyViewModel } from '../../../../../src/lib/view-models/base-type/date-time-property-view-model';
import { BaseEnumPropertyViewModel } from '../../../../../src/lib/view-models/base-type/enum-property-view-model';
import { StringPropertyViewModel } from '../../../../../src/lib/view-models/base-type/string-property-view-model';
import { BoolCellRendererComponent } from '../bool-cell-renderer/bool-cell-renderer.component';
import { DateCellRendererComponent } from '../date_cell_renderer.component';
import { DefaultCellRendererComponent } from '../default_cell_renderer.component';
import { EnumCellRendererComponent } from '../enum_cell_renderer.component';
import { ExternalCellRendererComponent } from '../external-cell-renderer/external-cell-renderer.component';
import { NumericCellRendererComponent } from '../numeric_cell_renderer.component';
import type { ColumnCellRendererParams, PropertyViewModelInterface } from '@nts/std/interfaces';
// create your cellEditor as a Angular component@UntilDestroy()
@UntilDestroy()
@Component({
    selector: 'nts-dynamic-text-box-renderer-cell',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    AsyncPipe,
    DefaultCellRendererComponent,
    BoolCellRendererComponent,
    DateCellRendererComponent,
    EnumCellRendererComponent,
    NumericCellRendererComponent,
    ExternalCellRendererComponent
],
    template: `@if (!params || params.data == null) {
  <nts-default-text-box-renderer-cell [params]="params"></nts-default-text-box-renderer-cell>
}
@if (isBool$ | async) {
  <nts-bool-text-box-renderer-cell [params]="params"></nts-bool-text-box-renderer-cell>
}
@if (isDate$ | async) {
  <nts-date-text-box-renderer-cell [params]="params"></nts-date-text-box-renderer-cell>
}
@if (isString$ | async) {
  <nts-default-text-box-renderer-cell [params]="params"></nts-default-text-box-renderer-cell>
}
@if (isEnum$ | async) {
  <nts-enum-text-box-renderer-cell [params]="params"></nts-enum-text-box-renderer-cell>
}
@if (isExternal$ | async) {
  <nts-external-renderer-cell [params]="params"></nts-external-renderer-cell>
}
@if (isNumeric$ | async) {
  <nts-numeric-text-box-renderer-cell [params]="params"></nts-numeric-text-box-renderer-cell>
}`,
    styleUrls: [`./dynamic-cell-renderer.component.scss`]
})
export class DynamicCellRendererComponent implements AgRendererComponent {

  isBool$ = new BehaviorSubject<boolean>(false);
  isDate$ = new BehaviorSubject<boolean>(false);
  isString$ = new BehaviorSubject<boolean>(false);
  isEnum$ = new BehaviorSubject<boolean>(false);
  isNumeric$ = new BehaviorSubject<boolean>(false);
  isExternal$ = new BehaviorSubject<boolean>(false);

  params: ICellRendererParams;

  agInit(params: ICellRendererParams): void {
    this.resetState();
    this.params = params;
    this.updateComponentType();
  }

  updateComponentType() {
    this.isBool$.next(false);
    this.isDate$.next(false);
    this.isString$.next(false);
    this.isEnum$.next(false);
    this.isNumeric$.next(false);
    this.isExternal$.next(false);

    if (this.params.data != null) {
      const externalPropertyTypeNames = [
        'ExternalCode-Numeric',
        'ExternalCode-String',
        'ExternalCodeRef',
        'ExternalDecode-Bool',
        'ExternalDecode-DateTime',
        'ExternalDecode-Enum',
        'ExternalDecode-Numeric',
        'ExternalDecode-String'
      ];

      if (this.propertyViewModel instanceof BaseBoolPropertyViewModel) {
        this.isBool$.next(true);
      } else if (this.propertyViewModel instanceof BaseDateTimePropertyViewModel) {
        this.isDate$.next(true);
      } else if (this.propertyViewModel instanceof BaseDateTimeOffsetPropertyViewModel) {
        this.isDate$.next(true);
      } else if (this.propertyViewModel instanceof StringPropertyViewModel) {
        this.isString$.next(true);
      } else if (this.propertyViewModel instanceof BaseEnumPropertyViewModel) {
        this.isEnum$.next(true);
      } else if (this.propertyViewModel instanceof BaseNumericPropertyViewModel) {
        this.isNumeric$.next(true);
      } else if (externalPropertyTypeNames.indexOf((this.params as ColumnCellRendererParams).columnInfo.propertyTypeName) > -1) {
        this.isExternal$.next(true);
      }
    }
  }

  refresh(params: ICellRendererParams) {
    this.resetState();
    this.params = params;
    this.updateComponentType();
    return true;
  }

  private resetState() {
    this._propertyViewModel = undefined;
    this.isBool$.next(false);
    this.isDate$.next(false);
    this.isString$.next(false);
    this.isEnum$.next(false);
    this.isNumeric$.next(false);
    this.isExternal$.next(false);
  }

  private _propertyViewModel: PropertyViewModelInterface;
  get propertyViewModel(): PropertyViewModelInterface {
    if (!this._propertyViewModel) {
      this._propertyViewModel = (this.params as ColumnCellRendererParams).columnInfo.fieldName.split('.').reduce(
        (o, i) => {
          if (i === 'value') {
            return o;
          }
          if (o != null) {
            return o[i];
          }
          return null;
        }, this.params.data) as PropertyViewModelInterface;
    }
    return this._propertyViewModel;
  }
}
